'use client';
import { useState } from 'react';
import type { PillarScores } from '@/lib/compute';
import { QUALITY_FACTORS } from '@/lib/clientScore';
import { isForwardFactor } from '@/lib/factor-classification';

const PILLAR_COLORS: Record<keyof PillarScores, string> = {
  profitability: '#34d399',
  growth: '#818cf8',
  safety: '#fbbf24',
  shareholderReturns: '#f472b6',
  momentum: '#f59e0b',
};

const PILLAR_LABELS: Record<keyof PillarScores, string> = {
  profitability: 'Profitability (45%)',
  growth: 'Growth (20%)',
  safety: 'Safety (15%)',
  shareholderReturns: 'Shr. Returns (20%)',
  momentum: 'Momentum',
};

const MODEL_DEFAULTS: Record<keyof PillarScores, number> = {
  profitability: 45, growth: 20, safety: 15, shareholderReturns: 20, momentum: 0,
};

const QUALITY_PILLARS: (keyof PillarScores)[] = ['profitability', 'growth', 'safety', 'shareholderReturns'];

interface Props {
  pillarWeights: Record<keyof PillarScores, number>;
  onPillarWeightChange: (pillar: keyof PillarScores, value: number) => void;
  onResetWeights: () => void;
  onPillarClick?: (pillar: string) => void;
}

export function PillarWeightsPanel({
  pillarWeights,
  onPillarWeightChange,
  onResetWeights,
  onPillarClick,
}: Props) {
  const [open, setOpen] = useState(false);

  const fwdPctFor = (pillar: keyof PillarScores): number | null => {
    const factors = QUALITY_FACTORS[pillar];
    if (!factors || factors.length === 0) return null;
    const fwd = factors.filter(f => isForwardFactor(String(f.key))).length;
    return Math.round((fwd / factors.length) * 100);
  };

  const isDefault = QUALITY_PILLARS.every(k => pillarWeights[k] === MODEL_DEFAULTS[k]);

  return (
    <div className="px-5 py-2 border-b border-[#1e293b]">
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full cursor-pointer">
        <label className="text-[#e2e8f0] text-[10px] font-semibold uppercase tracking-widest cursor-pointer">
          Pillar Weights
        </label>
        <svg className={`w-3 h-3 text-white transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="mt-3 space-y-2.5">
          {QUALITY_PILLARS.map(pillar => {
            const fwdPct = fwdPctFor(pillar);
            const bwPct = fwdPct != null ? 100 - fwdPct : null;
            return (
              <div key={pillar}>
                <div className="flex items-center justify-between mb-1">
                  <span className="flex items-baseline gap-1.5">
                    <span
                      className="text-[10px] font-semibold cursor-pointer hover:underline transition-colors"
                      style={{ color: PILLAR_COLORS[pillar] }}
                      onClick={(e) => { e.stopPropagation(); onPillarClick?.(pillar); }}
                      title={`View ${PILLAR_LABELS[pillar]} basket performance`}
                    >
                      {PILLAR_LABELS[pillar]}
                    </span>
                    {fwdPct != null && (
                      <span className="text-[8px] text-[#64748b] tabular-nums">
                        <span className="text-[#4ade80]">{fwdPct}%fwd</span> · <span className="text-[#94a3b8]">{bwPct}%bw</span>
                      </span>
                    )}
                  </span>
                  <span className="text-[10px] text-white tabular-nums" style={{ fontFamily: 'var(--font-mono)' }}>
                    {pillarWeights[pillar]}%
                  </span>
                </div>
                <input
                  type="range" min={0} max={100} value={pillarWeights[pillar]}
                  onChange={e => onPillarWeightChange(pillar, Number(e.target.value))}
                  className="w-full cursor-pointer h-1"
                  style={{ accentColor: PILLAR_COLORS[pillar] }}
                />
              </div>
            );
          })}
          {!isDefault && (
            <button
              onClick={onResetWeights}
              className="w-full py-1.5 text-[10px] font-semibold uppercase tracking-wider text-blue-400
                         border border-blue-400/30 rounded-lg hover:bg-blue-500/10 transition-colors duration-150 cursor-pointer mt-1"
            >
              Reset to Model Weight
            </button>
          )}
        </div>
      )}
    </div>
  );
}
